import { logger } from '@heidi/runtime/logger';

import {
  clearPendingWorkTaskLaunches,
  takePendingWorkTaskLaunches,
  type WorkHomeTaskLaunch,
} from './work-home-tasks';

/** App-owned task writes. This zone cannot import `@heidi/tasks`. */
export interface BindPendingWorkTaskLaunchPorts {
  markTaskInProgress: (taskId: string) => Promise<unknown>;
  linkTaskToWorkProject: (taskId: string, projectId: string) => Promise<unknown>;
  claimTaskOwner: (launch: WorkHomeTaskLaunch) => Promise<unknown>;
  invalidateClinicalTasks: () => Promise<unknown>;
}

/** Drop a stash when the Work chat that ordered it never bound. */
export function abandonPendingWorkTaskLaunches(): void {
  clearPendingWorkTaskLaunches();
}

async function claimOwnerQuietly(
  launch: WorkHomeTaskLaunch,
  ports: BindPendingWorkTaskLaunchPorts
): Promise<void> {
  try {
    await ports.claimTaskOwner(launch);
  } catch (error) {
    logger.error('Failed to assign a Heidi-delegated clinical task to its asker', {
      error_type: error instanceof Error ? error.name : 'unknown',
    });
  }
}

/**
 * Best-effort after the Work chat exists. One rejected task does not abandon
 * the rest. Link first so a partial failure stays `todo` + linked and can
 * retry the status write.
 */
// MOBILE-PARITY-DEBT(MUL-3651): B6 — claiming the launcher and starting work
// live on the client. Remove when the work-project link write does both.
export async function bindPendingWorkTaskLaunches(
  projectId: string,
  ports: BindPendingWorkTaskLaunchPorts
): Promise<void> {
  await bindWorkTaskLaunches(projectId, takePendingWorkTaskLaunches(), ports);
}

/** Same writes as `bindPendingWorkTaskLaunches` for launches the caller holds itself. */
export async function bindWorkTaskLaunches(
  projectId: string,
  launches: readonly WorkHomeTaskLaunch[],
  ports: BindPendingWorkTaskLaunchPorts
): Promise<void> {
  if (launches.length === 0) return;

  const results = await Promise.allSettled(
    launches.map(async (launch) => {
      await ports.linkTaskToWorkProject(launch.taskId, projectId);
      await claimOwnerQuietly(launch, ports);
      await ports.markTaskInProgress(launch.taskId);
    })
  );

  const failedCount = results.filter((result) => result.status === 'rejected').length;
  const succeeded = failedCount < results.length;
  if (succeeded) {
    await ports.invalidateClinicalTasks();
  }
  if (failedCount === 0) return;

  const firstRejected = results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  logger.error('Failed to link work-home tasks to project', {
    error_type:
      firstRejected?.reason instanceof Error ? firstRejected.reason.name : 'unknown',
    task_count: launches.length,
    failed_count: failedCount,
  });
}
